import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { toast } from 'sonner';
import {
  Briefcase, MapPin, LogOut, Plus, Loader2, FileText, UploadCloud, CheckCircle2, Clock, XCircle,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const AgentDashboard = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [me, setMe] = useState(null);
  const [roles, setRoles] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState('roles');
  const [loading, setLoading] = useState(true);
  const [submitFor, setSubmitFor] = useState(null); // role to submit a candidate for

  const authFetch = useCallback(async (path, opts = {}, tk) => {
    const t = tk || token;
    return fetch(`${API}${path}`, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${t}` } });
  }, [token]);

  const loadData = useCallback(async (tk) => {
    const [r1, r2] = await Promise.all([
      authFetch('/api/agencies/portal/roles', {}, tk),
      authFetch('/api/agencies/portal/submissions', {}, tk),
    ]);
    const d1 = await r1.json(); const d2 = await r2.json();
    setRoles(d1.roles || []); setSubs(d2.submissions || []);
  }, [authFetch]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      const tk = data.session?.access_token;
      if (!tk) { navigate('/agent/login'); return; }
      const meRes = await fetch(`${API}/api/agencies/me`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!meRes.ok) { navigate('/agent/login'); return; }
      const meData = await meRes.json();
      setToken(tk); setMe(meData);
      await loadData(tk);
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  const logout = async () => { await supabase.auth.signOut(); navigate('/agent/login'); };

  const refresh = () => loadData(token);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agent-dashboard-page">
      {/* Top bar */}
      <header className="bg-[#0A1A33] text-white">
        <div className="max-w-5xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-lg font-extrabold">Jobasti<span className="text-blue-400">IQ</span></span>
            <span className="text-xs text-slate-300 hidden sm:block">Agent portal · {me?.company}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-200 hidden sm:block">{me?.agent?.firstName} {me?.agent?.lastName}</span>
            <button onClick={logout} className="text-slate-300 hover:text-white p-2" data-testid="agent-logout"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6">
        <h1 className="text-2xl font-bold text-slate-900">Welcome, {me?.agent?.firstName || 'Agent'}</h1>
        <p className="text-sm text-slate-500 mb-5">Submit candidates for the roles <b>{me?.company}</b> has assigned to you.</p>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 border-b border-slate-200">
          {[['roles', `Open roles (${roles.length})`], ['subs', `My submissions (${subs.length})`]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-testid={`agent-tab-${k}`}>{label}</button>
          ))}
        </div>

        {tab === 'roles' && (
          roles.length === 0 ? (
            <div className="text-center py-16 text-slate-400"><Briefcase className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p className="text-sm">No roles assigned to you yet.</p></div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="agent-roles-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="py-3 px-4 font-semibold">Role</th>
                    <th className="py-3 px-3 font-semibold">Location</th>
                    <th className="py-3 px-3 font-semibold text-center">Submitted</th>
                    <th className="py-3 px-3 font-semibold text-center">In progress</th>
                    <th className="py-3 px-3 font-semibold text-center">Accepted</th>
                    <th className="py-3 px-4 font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((r) => (
                    <tr key={r.id} className="border-b border-slate-50 last:border-0" data-testid={`agent-role-${r.id}`}>
                      <td className="py-3 px-4 font-medium text-slate-900">{r.title}</td>
                      <td className="py-3 px-3 text-slate-500"><MapPin className="w-3 h-3 inline mr-1" />{r.location || '—'}</td>
                      <td className="py-3 px-3 text-center font-semibold text-slate-800">{r.submitted}</td>
                      <td className="py-3 px-3 text-center text-amber-600">{r.pending}</td>
                      <td className="py-3 px-3 text-center text-green-600">{r.accepted}</td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => setSubmitFor(r)} className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg" data-testid={`agent-submit-btn-${r.id}`}>
                          <Plus className="w-3.5 h-3.5" /> Submit candidate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {tab === 'subs' && (
          subs.length === 0 ? (
            <div className="text-center py-16 text-slate-400"><FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p className="text-sm">You haven't submitted any candidates yet.</p></div>
          ) : (
            <div className="space-y-2" data-testid="agent-submissions-list">
              {subs.map((s) => {
                const st = s.status === 'accepted' ? { icon: CheckCircle2, cls: 'bg-green-50 text-green-700', label: 'Accepted' }
                  : s.status === 'rejected' ? { icon: XCircle, cls: 'bg-rose-50 text-rose-700', label: 'Rejected' }
                  : { icon: Clock, cls: 'bg-blue-50 text-blue-700', label: 'In progress' };
                const StIcon = st.icon;
                return (
                  <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3" data-testid={`agent-sub-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900">{s.candidate?.firstName} {s.candidate?.lastName}</p>
                      <p className="text-xs text-slate-500">{s.positionTitle} · {s.candidate?.email}</p>
                      {s.status === 'rejected' && s.feedback && <p className="text-xs text-rose-600 mt-1 bg-rose-50 rounded-lg px-2 py-1"><b>Feedback:</b> {s.feedback}</p>}
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${st.cls}`}><StIcon className="w-3 h-3" /> {st.label}</span>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {submitFor && <SubmitModal role={submitFor} token={token} onClose={() => setSubmitFor(null)} onDone={() => { setSubmitFor(null); refresh(); setTab('subs'); }} />}
    </div>
  );
};

const SubmitModal = ({ role, token, onClose, onDone }) => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', linkedin: '', note: '' });
  const [file, setFile] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const addDocs = (fileList) => setDocs((prev) => [...prev, ...Array.from(fileList || [])]);
  const removeDoc = (i) => setDocs((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('positionId', role.id);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('cv', file);
      docs.forEach((d) => fd.append('documents', d));
      const r = await fetch(`${API}/api/agencies/portal/submissions`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || 'Submission failed');
      toast.success('Candidate submitted');
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="agent-submit-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-900">Submit candidate</h3>
        <p className="text-sm text-slate-500 mb-4">For <b>{role.title}</b></p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="First name *" value={form.firstName} onChange={(e) => upd('firstName', e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="agent-cand-firstname" />
            <input placeholder="Last name *" value={form.lastName} onChange={(e) => upd('lastName', e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="agent-cand-lastname" />
          </div>
          <input type="email" placeholder="Email *" value={form.email} onChange={(e) => upd('email', e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="agent-cand-email" />
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Phone" value={form.phone} onChange={(e) => upd('phone', e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="agent-cand-phone" />
            <input placeholder="LinkedIn URL" value={form.linkedin} onChange={(e) => upd('linkedin', e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="agent-cand-linkedin" />
          </div>
          <textarea placeholder="Note (why they're a great fit)" value={form.note} onChange={(e) => upd('note', e.target.value)} rows={3} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="agent-cand-note" />
          <label className="flex items-center gap-2 text-sm border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-blue-400" data-testid="agent-cand-cv-label">
            <UploadCloud className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500">{file ? file.name : 'Attach CV (PDF/Word) — used for the match score'}</span>
            <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="agent-cand-cv" />
          </label>
          <div>
            <label className="flex items-center gap-2 text-sm border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-blue-400" data-testid="agent-cand-docs-label">
              <FileText className="w-4 h-4 text-slate-400" />
              <span className="text-slate-500">Attach supporting documents (certificates, portfolio…) — optional, multiple</span>
              <input type="file" multiple className="hidden" onChange={(e) => addDocs(e.target.files)} data-testid="agent-cand-docs" />
            </label>
            {docs.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="agent-cand-docs-list">
                {docs.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                    <span className="truncate text-slate-600 flex items-center gap-1.5"><FileText className="w-3 h-3 text-slate-400" /> {d.name}</span>
                    <button onClick={() => removeDoc(i)} className="text-slate-400 hover:text-red-500 ml-2" data-testid={`agent-cand-doc-remove-${i}`}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm font-semibold px-5 py-2 rounded-lg flex items-center gap-2" data-testid="agent-cand-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
